// A small router: the URL is the state, links use history.pushState, routes are "/admin/users/:id" patterns.
import { createContext, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { updateWaiting } from './update-check'

interface Location {
  path: string
  search: URLSearchParams
}

const RouterCtx = createContext<{ loc: Location; navigate: (to: string, opts?: { replace?: boolean }) => void } | null>(null)

const current = (): Location => ({ path: window.location.pathname.replace(/\/+$/, '') || '/', search: new URLSearchParams(window.location.search) })

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(current)
  useEffect(() => {
    const onPop = () => setLoc(current())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  const value = useMemo(
    () => ({
      loc,
      navigate: (to: string, opts: { replace?: boolean } = {}) => {
        // A newer release is live: load the page in full, so the next page is on the new version.
        if (updateWaiting() && !opts.replace) return void window.location.assign(to)
        if (opts.replace) window.history.replaceState(null, '', to)
        else window.history.pushState(null, '', to)
        setLoc(current())
        if (!opts.replace) window.scrollTo(0, 0)
      },
    }),
    [loc],
  )
  return <RouterCtx.Provider value={value}>{children}</RouterCtx.Provider>
}

export function useRouter() {
  const r = useContext(RouterCtx)
  if (!r) throw new Error('useRouter outside RouterProvider')
  return r
}

/** Matches "/admin/users/:id" against a path; returns params or null. A trailing "/*" matches the rest. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean)
  const s = path.split('/').filter(Boolean)
  const params: Record<string, string> = {}
  for (let i = 0; i < p.length; i++) {
    if (p[i] === '*') {
      params['*'] = s.slice(i).join('/')
      return params
    }
    if (s[i] === undefined) return null
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(s[i])
    else if (p[i] !== s[i]) return null
  }
  return s.length === p.length ? params : null
}

export function Link({ to, children, ...rest }: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { navigate } = useRouter()
  return (
    <a
      href={to}
      {...rest}
      onClick={(e) => {
        rest.onClick?.(e)
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        e.preventDefault()
        navigate(to)
      }}
    >
      {children}
    </a>
  )
}

export function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter()
  useEffect(() => { navigate(to, { replace: true }) }, [to, navigate])
  return null
}
